/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Player, PlaylistController } from '@amzn/amp-playback'
import { createDisposer, logDebug, logWarn, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { guardedSkipForward } from '@/playback/skipGuard'
import { previousOrRestart } from './previousOrRestart'
import { withUserSkip } from '@/playback/metrics/userSkipSignal'
import { lockScreenMetadataInit } from '@/playback/lockScreenMetadata'

const { target, errorHandler } = ctx('mediaKeyBinding')

export interface MediaSessionBindingsDeps {
    readonly playlistController: PlaylistController
    readonly playerPromise: Promise<Player>
}

export function initializeMediaSessionBindings(deps: MediaSessionBindingsDeps): Unsubscribe {
    if (!('mediaSession' in navigator)) {
        logWarn(target, 'mediaSession not supported')
        return noop
    }
    const mediaSession = navigator.mediaSession
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const { playlistController, playerPromise } = deps
    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            logDebug(target, 'initializing media session bindings')
            add(
                playlistController.current.onData((track) => {
                    if (track == null) {
                        mediaSession.metadata = null
                    } else {
                        mediaSession.metadata = new MediaMetadata(lockScreenMetadataInit(track))
                    }
                })
            )
            mediaSession.setActionHandler('play', () => {
                player.play().catch(errorHandler)
            })

            mediaSession.setActionHandler('pause', () => {
                player.pause()
            })

            mediaSession.setActionHandler('previoustrack', () => {
                previousOrRestart(player, playlistController).catch(errorHandler)
            })

            mediaSession.setActionHandler('nexttrack', () => {
                if (!guardedSkipForward(playlistController)) return
                withUserSkip('next', () => playlistController.skipNext()).catch(errorHandler)
            })

            // Intentionally NOT registering 'seekbackward' / 'seekforward'.
            // iOS swaps the lock-screen / control-center transport controls
            // based on which handlers are bound — wiring those up replaces
            // the next/previous arrows with ±10s seek controls. The
            // product-level affordance we want is next/previous, so leave
            // seek* unbound on purpose.

            add(() => {
                mediaSession.setActionHandler('play', null)
                mediaSession.setActionHandler('pause', null)
                mediaSession.setActionHandler('previoustrack', null)
                mediaSession.setActionHandler('nexttrack', null)
            })
        })
        .catch(errorHandler)
    return dispose
}
